import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { 
  Plus, Package, Eye, MessageSquare, TrendingUp, Loader2, 
  Edit, Trash2, MoreVertical, CheckCircle, XCircle, Smartphone, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import StorefrontEditor from '../components/StorefrontEditor';
import PayoutsPanel from '../components/PayoutsPanel';
import AnalyticsPanel from '../components/AnalyticsPanel';
import InventoryPanel from '../components/InventoryPanel';
import PendingEnquiriesPanel from '../components/PendingEnquiriesPanel';
import PairPhonePanel from '../components/PairPhonePanel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { devLog, devError, devWarn } from '../utils/logger';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function SupplierDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [listings, setListings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('connect')) {
      return 'payouts';
    }
    return 'listings';
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, listingId: null });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboardRes, listingsRes, inquiriesRes] = await Promise.all([
          axios.get(`${API}/supplier/dashboard`),
          axios.get(`${API}/supplier/listings`),
          axios.get(`${API}/supplier/inquiries`)
        ]);
        setDashboard(dashboardRes.data);
        setListings(listingsRes.data);
        setInquiries(inquiriesRes.data);
      } catch (error) {
        devError('Error fetching dashboard:', error);
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleDelete = async (listingId) => {
    try {
      await axios.delete(`${API}/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(listings.filter(l => l.listing_id !== listingId));
      toast.success('Listing deleted');
    } catch (error) {
      toast.error('Failed to delete listing');
    }
    setDeleteDialog({ open: false, listingId: null });
  };

  const handleMarkSold = async (listingId) => {
    try {
      await axios.put(
        `${API}/listings/${listingId}`,
        { availability: 'sold' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setListings(listings.map(l => 
        l.listing_id === listingId ? { ...l, availability: 'sold' } : l
      ));
      toast.success('Listing marked as sold');
    } catch (error) {
      toast.error('Failed to update listing');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="supplier-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Pending-verification banner — shown until a PatinaMart admin has
            approved this supplier. Listings created before approval are saved
            but hidden from buyers. */}
        {user && user.user_type === 'supplier' && user.verified_supplier !== true && (
          <div
            className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 md:p-5 flex items-start gap-4"
            data-testid="verification-pending-banner"
          >
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
              <Loader2 className="w-5 h-5 text-amber-300 animate-spin" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.18em] text-amber-300 mb-0.5">Awaiting verification</p>
              <p className="text-sm md:text-base text-foreground font-medium mb-1">
                Your listings aren't visible to buyers yet.
              </p>
              <p className="text-sm text-muted-foreground">
                The PatinaMart team reviews new suppliers manually — usually within 48 hours. You'll
                get an email the moment you're approved. In the meantime, feel free to set up your
                storefront and draft listings so you're ready to go live.
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Dashboard</p>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-foreground">
              Welcome, {user?.name?.split(' ')[0]}
            </h1>
          </div>
          <Button 
            onClick={() => navigate('/create-listing')}
            className="btn-gold"
            data-testid="create-listing-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Listing
          </Button>
        </div>

        {/* Wingman discoverability banner — shown until the supplier pairs a phone.
            Clicking jumps to the existing PairPhonePanel inside the Listings tab. */}
        <Link
          to="/supplier-app"
          className="group block mb-6 rounded-xl border border-accent/30 bg-gradient-to-r from-accent/10 via-accent/5 to-transparent p-4 md:p-5 hover:border-accent/50 transition-colors"
          data-testid="wingman-promo-banner"
        >
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex w-11 h-11 rounded-lg bg-accent/15 border border-accent/30 items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5 text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.18em] text-accent mb-0.5">New — Supplier Wingman</p>
              <p className="text-sm md:text-base text-foreground font-medium">
                Run your yard from your pocket — instant enquiry alerts &amp; one-tap listings on your phone.
              </p>
            </div>
            <span className="hidden md:inline-flex items-center gap-1 text-sm text-accent group-hover:gap-2 transition-all">
              Learn more <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="p-6 rounded-lg bg-card/50 border border-border/20">
            <Package className="w-6 h-6 text-accent mb-3" />
            <p className="text-2xl font-bold text-foreground" data-testid="total-listings">
              {dashboard?.total_listings || 0}
            </p>
            <p className="text-sm text-muted-foreground">Total Listings</p>
          </div>
          <div className="p-6 rounded-lg bg-card/50 border border-border/20">
            <CheckCircle className="w-6 h-6 text-emerald-400 mb-3" />
            <p className="text-2xl font-bold text-foreground" data-testid="available-listings">
              {dashboard?.available_listings || 0}
            </p>
            <p className="text-sm text-muted-foreground">Available</p>
          </div>
          <div className="p-6 rounded-lg bg-card/50 border border-border/20">
            <Eye className="w-6 h-6 text-blue-400 mb-3" />
            <p className="text-2xl font-bold text-foreground" data-testid="total-views">
              {dashboard?.total_views || 0}
            </p>
            <p className="text-sm text-muted-foreground">Total Views</p>
          </div>
          <div className="p-6 rounded-lg bg-card/50 border border-border/20">
            <MessageSquare className="w-6 h-6 text-primary mb-3" />
            <p className="text-2xl font-bold text-foreground" data-testid="total-inquiries">
              {dashboard?.total_inquiries || 0}
            </p>
            <p className="text-sm text-muted-foreground">Inquiries</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-border/20 mb-6">
          <button
            onClick={() => setActiveTab('listings')}
            className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'listings'
                ? 'border-accent text-accent'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="listings-tab"
          >
            My Listings ({listings.length})
          </button>
          <button
            onClick={() => setActiveTab('inquiries')}
            className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'inquiries'
                ? 'border-accent text-accent'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="inquiries-tab"
          >
            Inquiries ({inquiries.length})
          </button>
          <button
            onClick={() => setActiveTab('storefront')}
            className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'storefront'
                ? 'border-accent text-accent'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="storefront-tab"
          >
            Storefront
          </button>
          <button
            onClick={() => setActiveTab('payouts')}
            className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'payouts'
                ? 'border-accent text-accent'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="payouts-tab"
          >
            Payouts
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'analytics'
                ? 'border-accent text-accent'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="analytics-tab"
          >
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('inventory')}
            className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'inventory'
                ? 'border-accent text-accent'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="inventory-tab"
          >
            Inventory
          </button>
        </div>

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div className="space-y-6">
            <PendingEnquiriesPanel />
            <PairPhonePanel />
            <div className="space-y-4" data-testid="listings-list">
            {listings.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No listings yet. Create your first one!</p>
                <Button onClick={() => navigate('/create-listing')} className="btn-gold">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Listing
                </Button>
              </div>
            ) : (
              listings.map((listing) => (
                <div 
                  key={listing.listing_id}
                  className="flex flex-col md:flex-row gap-4 p-4 rounded-lg bg-card/50 border border-border/20 hover:border-border/40 transition-colors"
                  data-testid={`listing-row-${listing.listing_id}`}
                >
                  {/* Image */}
                  <div className="w-full md:w-32 h-24 rounded-md overflow-hidden bg-secondary flex-shrink-0">
                    {listing.images?.[0] ? (
                      <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-8 h-8 text-muted-foreground/30" />
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-foreground line-clamp-1">{listing.title}</h3>
                        <p className="text-sm text-muted-foreground capitalize">{listing.category}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 badge-${listing.availability}`}>
                        {listing.availability}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="font-semibold text-accent">{formatPrice(listing.price)}</span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" /> {listing.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-4 h-4" /> {listing.inquiries}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/edit-listing/${listing.listing_id}`)}
                      className="border-border/40"
                      data-testid={`edit-btn-${listing.listing_id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="border-border/40">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-card border-border">
                        <DropdownMenuItem onClick={() => navigate(`/listing/${listing.listing_id}`)}>
                          View Listing
                        </DropdownMenuItem>
                        {listing.availability === 'available' && (
                          <DropdownMenuItem onClick={() => handleMarkSold(listing.listing_id)}>
                            Mark as Sold
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => setDeleteDialog({ open: true, listingId: listing.listing_id })}
                          className="text-destructive"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))
            )}
          </div>
          </div>
        )}

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <div className="space-y-4" data-testid="inquiries-list">
            {inquiries.length === 0 ? (
              <div className="text-center py-16">
                <MessageSquare className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No inquiries yet.</p>
              </div>
            ) : (
              inquiries.map((inquiry) => (
                <div 
                  key={inquiry.inquiry_id}
                  className="p-4 rounded-lg bg-card/50 border border-border/20"
                  data-testid={`inquiry-${inquiry.inquiry_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-4">
                      {inquiry.listing_image && (
                        <img 
                          src={inquiry.listing_image} 
                          alt="" 
                          className="w-16 h-16 rounded-md object-cover"
                        />
                      )}
                      <div>
                        <p className="font-semibold text-foreground">{inquiry.listing_title}</p>
                        <p className="text-sm text-accent">{inquiry.buyer_name}</p>
                        <p className="text-sm text-muted-foreground">{inquiry.buyer_email}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full badge-${inquiry.status === 'pending' ? 'pending' : 'available'}`}>
                      {inquiry.status}
                    </span>
                  </div>
                  {inquiry.message && (
                    <p className="mt-3 text-sm text-muted-foreground bg-secondary/30 p-3 rounded-md">
                      "{inquiry.message}"
                    </p>
                  )}
                  <p className="mt-2 text-xs text-muted-foreground">
                    {new Date(inquiry.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Storefront Tab */}
        {activeTab === 'storefront' && (
          <div data-testid="storefront-tab-content">
            <StorefrontEditor />
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <div data-testid="payouts-tab-content">
            <PayoutsPanel />
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div data-testid="analytics-tab-content">
            <AnalyticsPanel />
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <div data-testid="inventory-tab-content">
            <InventoryPanel />
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Listing</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this listing? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteDialog.listingId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
